import { useMemo } from 'react';
import { normalizeIdeaCards } from '@/lib/idea-card-adapter';
import { parseHeadingSectionPhase } from '@/lib/idea-card-heading-section-phase';
import { buildCardsAndRemainder, buildCardsAndRemainderWithOptions } from '@/lib/idea-parser';
import { createIdeaCardParserContext } from '@/lib/idea-card-parser-preflight';
import { createMarkdownCard } from '@/lib/idea-card-factory';
import type { NormalizedIdeaCardData } from '@/lib/idea-card-types';
import type { Message } from '@/types/chat';

export interface NormalizedMessageCardsResult {
  cards: NormalizedIdeaCardData[];
  remainder: string;
}

const HORIZONTAL_CARD_PLACEHOLDER_RE = /\[HORIZONTAL_CARD:(\d+)\]/g;

const collectHorizontalCardIndices = (cards: Array<Pick<NormalizedIdeaCardData, 'layout'>>): number[] =>
  cards.reduce<number[]>((indices, card, index) => {
    if (card.layout === 'horizontal') {
      indices.push(index);
    }

    return indices;
  }, []);

/**
 * Removes group header lines that appear before horizontal card placeholders.
 * Examples: "3 rekomendasi bio Instagram:" or "10 variasi username:"
 */
const removeGroupHeaders = (text: string): string => {
  const lines = text.split('\n');
  const result: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Check if this line is a potential group header (ends with colon, contains number)
    const isGroupHeader = /^.+[:\uFF1A]\s*$/.test(trimmed) && /\d/.test(trimmed);

    if (isGroupHeader) {
      // Look ahead to see if there's a placeholder after blank lines
      let nextNonEmptyIdx = i + 1;
      while (nextNonEmptyIdx < lines.length && lines[nextNonEmptyIdx].trim() === '') {
        nextNonEmptyIdx++;
      }

      const nextNonEmpty = nextNonEmptyIdx < lines.length ? lines[nextNonEmptyIdx].trim() : '';
      const hasPlaceholderAfter = HORIZONTAL_CARD_PLACEHOLDER_RE.test(nextNonEmpty);

      // Reset regex lastIndex after test
      HORIZONTAL_CARD_PLACEHOLDER_RE.lastIndex = 0;

      if (hasPlaceholderAfter) {
        // Skip this group header line
        continue;
      }
    }

    result.push(line);
  }

  return result.join('\n').trim();
};

const normalizeSectionKey = (value: string): string =>
  value.trim().toLowerCase().replace(/\s+/g, ' ');

const extractHeadingKey = (line: string): string | null => {
  const trimmed = line.trim();
  if (!trimmed) {
    return null;
  }

  const cleaned = trimmed.replace(/^\s*[\*_`~]+|[\*_`~]+\s*$/g, '').trim();
  const inlineMatch = cleaned.match(/^([^:\uFF1A\n]{2,100})[:\uFF1A]\s+.+$/);
  if (inlineMatch?.[1]) {
    return normalizeSectionKey(inlineMatch[1]);
  }

  const titleOnlyMatch = cleaned.match(/^([^:\uFF1A\n]{2,100})[:\uFF1A]\s*$/);
  if (titleOnlyMatch?.[1]) {
    return normalizeSectionKey(titleOnlyMatch[1]);
  }

  return null;
};

const getHorizontalCardSectionLabels = (card: NormalizedIdeaCardData): string[] => {
  if (card.layout !== 'horizontal' || card.bodyModel !== 'sectioned_horizontal') {
    return [];
  }

  return card.bodySections
    .map((section) => section.label ? normalizeSectionKey(section.label) : '')
    .filter(Boolean);
};

const findMatchingHeadingSectionRange = (
  content: string,
  card: NormalizedIdeaCardData
): { start: number; end: number } | null => {
  const labels = getHorizontalCardSectionLabels(card);
  if (labels.length < 2) {
    return null;
  }

  const parsed = parseHeadingSectionPhase(createIdeaCardParserContext(content));
  if (!parsed || parsed.cards.length === 0) {
    return null;
  }

  const titles = parsed.cards.map((sectionCard) => normalizeSectionKey(sectionCard.title));
  let bestMatch: { start: number; end: number; matchCount: number; labelStartIndex: number } | null = null;

  for (let titleIndex = 0; titleIndex < titles.length; titleIndex++) {
    const labelStartIndex = labels.indexOf(titles[titleIndex]);
    if (labelStartIndex === -1) {
      continue;
    }

    let nextTitleIndex = titleIndex;
    let nextLabelIndex = labelStartIndex;
    let matchCount = 0;

    while (
      nextTitleIndex < titles.length &&
      nextLabelIndex < labels.length &&
      titles[nextTitleIndex] === labels[nextLabelIndex]
    ) {
      matchCount++;
      nextTitleIndex++;
      nextLabelIndex++;
    }

    if (matchCount < 2) {
      continue;
    }

    if (!bestMatch || matchCount > bestMatch.matchCount) {
      bestMatch = {
        start: titleIndex,
        end: nextTitleIndex - 1,
        matchCount,
        labelStartIndex,
      };
    }
  }

  if (!bestMatch) {
    return null;
  }

  const startRange = parsed.usedRanges[bestMatch.start];
  const endRange = parsed.usedRanges[bestMatch.end];

  if (!startRange || !endRange) {
    return null;
  }

  let expandedStart = startRange.start;
  if (bestMatch.labelStartIndex > 0) {
    const previousLabel = labels[bestMatch.labelStartIndex - 1];
    const lines = content.split(/\r?\n/);

    for (let index = startRange.start - 1; index >= Math.max(0, startRange.start - 12); index--) {
      const headingKey = extractHeadingKey(lines[index] || '');
      if (headingKey === previousLabel) {
        expandedStart = index;
        break;
      }
    }
  }

  return {
    start: expandedStart,
    end: endRange.end,
  };
};

const replaceUnmatchedHorizontalSections = (
  content: string,
  indices: number[],
  normalizedCards: NormalizedIdeaCardData[]
): { content: string; matchedIndices: number[] } => {
  let nextContent = content.trim();
  const matchedIndices: number[] = [];

  for (const index of indices) {
    const card = normalizedCards[index];
    if (!card) {
      continue;
    }

    const matchingRange = findMatchingHeadingSectionRange(nextContent, card);
    if (!matchingRange) {
      continue;
    }

    const lines = nextContent.split(/\r?\n/);
    nextContent = [
      ...lines.slice(0, matchingRange.start),
      `[HORIZONTAL_CARD:${index}]`,
      ...lines.slice(matchingRange.end + 1),
    ]
      .join('\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    matchedIndices.push(index);
  }

  return {
    content: nextContent,
    matchedIndices,
  };
};

const appendHorizontalCardPlaceholders = (content: string, indices: number[]): string => {
  if (indices.length === 0) {
    return content.trim();
  }

  const trailingPlaceholders = indices
    .map((index) => `[HORIZONTAL_CARD:${index}]`)
    .join('\n\n');

  return [content.trim(), trailingPlaceholders].filter(Boolean).join('\n\n');
};

const buildStructuredCardsRemainder = (
  content: string,
  normalizedCards: NormalizedIdeaCardData[]
): string => {
  const actualHorizontalIndices = collectHorizontalCardIndices(normalizedCards);
  const horizontalCardsByTitle = normalizedCards.reduce<Map<string, NormalizedIdeaCardData[]>>((groups, card) => {
    if (card.layout === 'horizontal') {
      const key = card.title.trim().toLowerCase();
      const existing = groups.get(key) || [];
      groups.set(key, [...existing, card]);
    }

    return groups;
  }, new Map());

  const parsed = buildCardsAndRemainderWithOptions(content, {
    matchCard: (card) => {
      const key = card.title.trim().toLowerCase();
      const matches = horizontalCardsByTitle.get(key);
      const matchedCard = matches?.shift();
      if (!matchedCard) {
        return null;
      }

      return createMarkdownCard({
        title: matchedCard.title,
        body: 'matched structured horizontal card',
        layout: 'horizontal',
      });
    },
  });

  const parsedHorizontalIndices = parsed.cards.reduce<number[]>((indices, card, index) => {
    if (card.layout === 'horizontal') {
      indices.push(index);
    }

    return indices;
  }, []);

  const parsedHorizontalOrderByIndex = new Map<number, number>();
  parsedHorizontalIndices.forEach((index, order) => {
    parsedHorizontalOrderByIndex.set(index, order);
  });

  const remappedRemainder = parsed.remainder.replace(HORIZONTAL_CARD_PLACEHOLDER_RE, (_, rawIndex: string) => {
    const placeholderIndex = Number(rawIndex);
    const horizontalOrder = parsedHorizontalOrderByIndex.get(placeholderIndex);

    if (horizontalOrder === undefined) {
      return '';
    }

    const actualIndex = actualHorizontalIndices[horizontalOrder];
    return actualIndex !== undefined ? `[HORIZONTAL_CARD:${actualIndex}]` : '';
  }).trim();

  const baseRemainder = parsed.cards.length > 0 ? remappedRemainder : content.trim();
  const unmatchedHorizontalIndices = actualHorizontalIndices.slice(parsedHorizontalIndices.length);
  const sectionReplacementResult = replaceUnmatchedHorizontalSections(
    baseRemainder,
    unmatchedHorizontalIndices,
    normalizedCards
  );
  const cleanedRemainder = removeGroupHeaders(sectionReplacementResult.content);
  const remainingHorizontalIndices = unmatchedHorizontalIndices.filter(
    (index) => !sectionReplacementResult.matchedIndices.includes(index)
  );

  return appendHorizontalCardPlaceholders(cleanedRemainder, remainingHorizontalIndices);
};

/**
 * Inserts card placeholders at their original positions based on cardBlocks.
 * This preserves inline positioning from the agent's output.
 * Only creates placeholders for 'horizontal' layout cards - 'title' and 'vertical' cards are rendered separately.
 */
const buildInlineCardsRemainder = (
  content: string,
  normalizedCards: NormalizedIdeaCardData[],
  cardBlocks: Array<{ cards: any[]; position: number }>
): string => {
  if (cardBlocks.length === 0) {
    // Fallback to old behavior (try to match based on content)
    return buildStructuredCardsRemainder(content, normalizedCards);
  }

  // Sort blocks by position (descending) to insert from end to start
  const sortedBlocks = [...cardBlocks].sort((a, b) => b.position - a.position);

  let result = content;
  let cardIndex = normalizedCards.length - 1;

  for (const block of sortedBlocks) {
    const blockSize = block.cards.length;
    const placeholders: string[] = [];

    // Generate placeholders for this block's cards (in reverse)
    // Only create placeholders for horizontal layout cards
    for (let i = blockSize - 1; i >= 0; i--) {
      if (cardIndex >= 0) {
        const card = normalizedCards[cardIndex];
        // Only add placeholder if this is a horizontal card
        if (card && card.layout === 'horizontal') {
          placeholders.unshift(`[HORIZONTAL_CARD:${cardIndex}]`);
        }
        cardIndex--;
      }
    }

    if (placeholders.length > 0) {
      // Insert placeholders at the block's position
      const before = result.slice(0, block.position);
      const after = result.slice(block.position);
      result = `${before}\n\n${placeholders.join('\n\n')}\n\n${after}`;
    }
  }

  // Clean up excessive newlines
  return result.replace(/\n{3,}/g, '\n\n').trim();
};

export function useNormalizedMessageCards(
  message: Pick<Message, 'content' | 'cards' | 'cardBlocks'> | null | undefined,
  options: { enabled?: boolean } = {}
): NormalizedMessageCardsResult {
  const enabled = options.enabled ?? true;

  return useMemo(() => {
    const content = message?.content || '';

    if (!enabled || !message) {
      return { cards: [], remainder: content };
    }

    if (Array.isArray(message.cards) && message.cards.length > 0) {
      const normalizedCards = normalizeIdeaCards(message.cards);

      // Use cardBlocks if available for inline positioning
      const cardBlocks = message.cardBlocks;

      if (Array.isArray(cardBlocks) && cardBlocks.length > 0) {
        return {
          cards: normalizedCards,
          remainder: buildInlineCardsRemainder(content, normalizedCards, cardBlocks),
        };
      }

      // Fallback to content-based matching
      return {
        cards: normalizedCards,
        remainder: buildStructuredCardsRemainder(content, normalizedCards),
      };
    }

    const parsed = buildCardsAndRemainder(content);
    return { cards: normalizeIdeaCards(parsed.cards), remainder: parsed.remainder };
  }, [enabled, message?.cards, message?.cardBlocks, message?.content]);
}
